// Copyright (c) StrangeBee 2023

import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ICONS } from '@Constants/iconsApp';
import { useLocalMessage } from '@Helpers/locale';
import { ConfigList } from '@Hooks/useConfigList';
import { useDrawer } from '@Hooks/useDrawer';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatDate } from '@Molecules/Format';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { useListAllFields } from '@Queries/describeQueries';
import { Badge } from 'antd';
import { ReactElement, useEffect, useState } from 'react';
import { AuditOutput, ObjectTypeV1 } from 'thehive-sdk';
import { AuditDetails } from '../AuditDetails/AuditDetails';

import './AuditList.scss';

interface AuditListProps {
    config: ConfigList<AuditOutput['base']>;
}

export const AuditList = ({ config }: AuditListProps) => {
    const i = useLocalMessage();
    const { data: describeData } = useListAllFields();
    const { visible, setVisible, data, setData } = useDrawer({});

    const {
        storagePageManager,
        storageViewManager,
        dataSource = [],
        columnsRenderer,
        listRefetch,
        countRefetch,
        filterManager,
        paginationManager,
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        table,
    } = config;

    const { isLoading } = table;

    const { page: storagePage, setAutoRefresh, setFilterIsOpen, setOnLoadView } = storagePageManager || { page: null };

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh || false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
    });

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    const actions: Record<
        AuditOutput['base']['action'],
        {
            label: string;
            icon: ReactElement;
        }
    > = {
        create: {
            label: i('common.create'),
            icon: <ICONS.CreateIcon />,
        },
        merge: {
            label: i('common.merge'),
            icon: <ICONS.MergeIcon />,
        },
        update: {
            label: i('common.update'),
            icon: <ICONS.UpdateDateIcon />,
        },
        delete: {
            label: i('common.delete'),
            icon: <ICONS.DeleteIcon />,
        },
    };

    const columns = columnsRenderer([
        {
            title: [{ label: i('common.dates'), sortField: '_createdAt' }],
            className: 'audit-list__column-date',
            render: ({ _createdAt = 0 }, { date: filterDate }) => (
                <FormatDate date={_createdAt} onClick={(): void => filterDate(_createdAt, '_createdAt')} />
            ),
        },
        {
            title: 'Action',
            className: 'audit-list__column-action',
            render: (obj, { custom: filterCustom }) => (
                <THButton
                    type="link"
                    className="audit-list__action__link"
                    onClick={() =>
                        filterCustom({
                            field: 'operation',
                            operator: 'any',
                            values: [obj.action],
                        })
                    }
                >
                    {actions[obj.action].label}
                </THButton>
            ),
        },
        {
            title: 'Field',
            className: 'audit-list__column-field',
            render: ({ details }) => {
                const keys = Object.keys(details);

                const label = i(`history.change${keys.length > 1 ? 's' : ''}`);

                return (
                    <div className="audit-list__field">
                        <span
                            className="audit-list__field__open"
                            onClick={() => {
                                setVisible(true);
                                setData(details);
                            }}
                        >
                            <div className="audit-list__field-strong">{`${keys.length} ${label}`}</div>
                            <div className="audit-list__field-italic">{keys.join(', ')}</div>
                        </span>
                    </div>
                );
            },
        },
        {
            title: i('common.user'),
            className: 'audit-list__column-user',
            render: ({ _createdBy }): ReactElement => <AssigneeInfo assignee={_createdBy} avatarOnly />,
        },
    ]);

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        {!storagePage?.autoRefresh && (
                            <Badge dot={needRefresh}>
                                <THTooltip
                                    title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                    placement="bottom"
                                >
                                    <THButton
                                        size="small"
                                        type={needRefresh ? 'primary' : 'default'}
                                        shape="circle"
                                        onClick={() => {
                                            listRefetch();
                                            countRefetch();
                                            setNeedRefresh(false);
                                        }}
                                    >
                                        <ICONS.SyncOutlined />
                                    </THButton>
                                </THTooltip>
                            </Badge>
                        )}
                        <ManageSwitch
                            label={i('common.auto-refresh')}
                            value={localAutoRefresh}
                            onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                            icon={<ICONS.SyncOutlined />}
                        />

                        <ManageSwitch
                            label={i('common.filters')}
                            value={localFilterIsOpen}
                            onChange={(editFilters): void => setLocalFilters(editFilters)}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describeData?.[ObjectTypeV1.AUDIT]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    columns={columns}
                    dataSource={dataSource}
                    rowKey="_id"
                    emptyPlaceholder={<DisplayEmptyData spin={isLoading} label={i('history.none')} />}
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>
            <AuditDetails visible={visible} data={data} setVisible={setVisible} />
        </>
    );
};
